import { LitElement, html } from 'lit';
import { property } from 'lit/decorators.js';
import { SDSEventDispatcherMixin } from '../mixins/sds-event-dispatcher-mixin';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-stepper-step.styles';

export interface SDSStepperStepProps {
	complete?: boolean;
	interactive?: boolean;
	active?: boolean;
}

@guardedCustomElement('sds-stepper-step')
export class SDSStepperStep extends SDSEventDispatcherMixin(LitElement) {
	static override styles = [styles];
	public readonly internals: ElementInternals;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	@property({ type: Boolean, reflect: true }) complete: SDSStepperStepProps['complete'] = false;
	@property({ type: Boolean, reflect: true }) interactive: SDSStepperStepProps['interactive'] = false;
	@property({ type: Boolean, reflect: true }) active: SDSStepperStepProps['active'] = false;

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	private readonly _handleClick = (event: MouseEvent) => {
		event.preventDefault();
		event.stopImmediatePropagation();

		this.dispatchSheriffEvent('_sds-step-clicked');
	};

	private readonly _renderNonInteractiveStep = () => {
		return html`<div class="sds-stepper-step__step--static">
			<span class="sds-stepper-step__counter"></span>
			<div class="sds-stepper-step__label"><slot></slot></div>
		</div>`;
	};

	private readonly _renderInteractiveStep = () => {
		return html`<button type="button" role="tab" aria-selected="${this.active ? 'true' : 'false'}" id="button" class="sds-stepper-step__step" @click="${this._handleClick}">
			<span class="sds-stepper-step__counter"></span>
			<div class="sds-stepper-step__label"><slot></slot></div>
		</button>`;
	};

	override render() {
		if (this.interactive) {
			return html`<div class="sds-stepper-step">
				<span class="sds-stepper-step__divider sds-stepper-step__divider--before"></span>
				${this._renderInteractiveStep()}
				<span class="sds-stepper-step__divider sds-stepper-step__divider--after"></span>
			</div>`;
		}

		return html`<div class="sds-stepper-step" role="tab" aria-disabled="true" tabindex="-1" aria-selected="false">
			<span class="sds-stepper-step__divider sds-stepper-step__divider--before"></span>
			${this._renderNonInteractiveStep()}
			<span class="sds-stepper-step__divider sds-stepper-step__divider--after"></span>
		</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-stepper-step': SDSStepperStep;
	}
}
